import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Download, Eye, Calendar, RefreshCw, CheckCircle, Clock, XCircle, Loader2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/services/api';
import { formatDateTime } from '@/lib/utils';
import { AiAssistantPanel } from '@/components/ai/AiAssistantPanel';
import { useAuth } from '@/contexts/AuthContext';
import type { RecordItem, Document } from '@/types';

interface DocumentsViewProps {
  prisonerId: string;
  highlightDocumentId?: string;
  highlightRecordItemId?: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function toBrowserAccessibleMinioUrl(url?: string): string | undefined {
  if (!url) return undefined;
  return url.replace('http://minio:9000', `${window.location.origin}/minio`);
}

export function DocumentsView({
  prisonerId,
  highlightDocumentId,
  highlightRecordItemId,
}: DocumentsViewProps) {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canUseAi = hasPermission('ai.assistant.use');

  const { data: records, isLoading, error } = useQuery({
    queryKey: ['records', prisonerId, 'DOCUMENT'],
    queryFn: () => api.getRecords(prisonerId, {}),
  });

  const { data: ocrStatus } = useQuery({
    queryKey: ['ocr-status'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/documents/ocr-status`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to fetch OCR status');
      return response.json() as Promise<Record<string, number>>;
    },
  });

  const reprocessOcrMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/documents/reprocess-ocr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to reprocess OCR');
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'OCR-Verarbeitung gestartet',
        description: data.message,
      });
      queryClient.invalidateQueries({ queryKey: ['ocr-status'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Filter only document type records
  const documents = records?.filter((r: RecordItem) => r.type === 'DOCUMENT') || [];

  useEffect(() => {
    if (!highlightDocumentId && !highlightRecordItemId) return;

    const targetId = highlightRecordItemId
      ? `record-item-${highlightRecordItemId}`
      : `document-file-${highlightDocumentId}`;
    const element = document.getElementById(targetId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [documents, highlightDocumentId, highlightRecordItemId]);

  const isRecordHighlighted = (recordItemId: string) => highlightRecordItemId === recordItemId;
  const isFileHighlighted = (fileId: string) => highlightDocumentId === fileId;
  const isRecordRowHighlighted = (recordItem: RecordItem) =>
    isRecordHighlighted(recordItem.id) ||
    recordItem.documents?.some((file) => isFileHighlighted(file.id)) ||
    false;

  const handleDownload = async (documentId: string, filename: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/documents/${documentId}/download`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      
      const data = await response.json();
      if (data.url) {
        const browserUrl = toBrowserAccessibleMinioUrl(data.url);
        if (browserUrl) {
          window.open(browserUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const getNeedleColor = (needle: number) => {
    switch (needle) {
      case 1: return 'bg-blue-500';
      case 2: return 'bg-green-500';
      case 3: return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const getNeedleLabel = (needle: number) => {
    switch (needle) {
      case 1: return 'Nadel 1 - Grundunterlagen';
      case 2: return 'Nadel 2 - Vollstreckung';
      case 3: return 'Nadel 3 - Sonstige';
      default: return `Nadel ${needle}`;
    }
  };

  if (isLoading) {
    return <p className="text-muted-foreground">Lädt Dokumente...</p>;
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-500">
          Fehler beim Laden: {(error as Error).message}
        </CardContent>
      </Card>
    );
  }

  if (!documents.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <FileText className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">Keine Dokumente vorhanden.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Klicken Sie auf "Dokument hinzufügen" um ein Dokument hochzuladen.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Group documents by needle
  const groupedByNeedle = documents.reduce((acc: Record<number, RecordItem[]>, doc: RecordItem) => {
    const needle = doc.needle || 3;
    if (!acc[needle]) acc[needle] = [];
    acc[needle].push(doc);
    return acc;
  }, {});

  const getOcrStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'PROCESSING': return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'PENDING': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'FAILED': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{documents.length}</div>
            <p className="text-sm text-muted-foreground">Dokumente gesamt</p>
          </CardContent>
        </Card>
        {[1, 2, 3].map((needle) => (
          <Card key={needle}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${getNeedleColor(needle)}`} />
                <span className="text-2xl font-bold">
                  {groupedByNeedle[needle]?.length || 0}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">Nadel {needle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* OCR Status */}
      {ocrStatus && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">OCR-Texterkennung</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => reprocessOcrMutation.mutate()}
                disabled={reprocessOcrMutation.isPending}
              >
                {reprocessOcrMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Ausstehende verarbeiten
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm">
                  <span className="font-medium">{ocrStatus.COMPLETED || 0}</span> Abgeschlossen
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 text-blue-500" />
                <span className="text-sm">
                  <span className="font-medium">{ocrStatus.PROCESSING || 0}</span> In Bearbeitung
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-yellow-500" />
                <span className="text-sm">
                  <span className="font-medium">{ocrStatus.PENDING || 0}</span> Ausstehend
                </span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm">
                  <span className="font-medium">{ocrStatus.FAILED || 0}</span> Fehlgeschlagen
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {canUseAi && (
        <AiAssistantPanel
          title="KI-Fragen zu dieser Akte"
          description="Kontext ist auf den aktuellen Gefangenen voreingestellt."
          initialScope="documents"
          prisonerId={prisonerId}
        />
      )}

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Alle Dokumente</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {documents.map((doc: RecordItem) => (
              <div
                key={doc.id}
                id={`record-item-${doc.id}`}
                className={`flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50 ${
                  isRecordRowHighlighted(doc) ? 'border-primary bg-primary/5 ring-2 ring-primary/30' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`mt-1 h-10 w-10 rounded-lg ${getNeedleColor(doc.needle)}/10 flex items-center justify-center`}>
                    <FileText className={`h-5 w-5 text-${doc.needle === 1 ? 'blue' : doc.needle === 2 ? 'green' : 'orange'}-600`} />
                  </div>
                  <div>
                    <h4 className="font-medium">{doc.title}</h4>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <span className={`h-2 w-2 rounded-full ${getNeedleColor(doc.needle)}`} />
                        {getNeedleLabel(doc.needle)}
                      </span>
                      {doc.needle === 2 && doc.caseSequenceNumber && (
                        <span className="font-mono">
                          Seq: {doc.caseSequenceNumber}
                        </span>
                      )}
                      {doc.needle === 3 && doc.folioNumber && (
                        <span className="font-mono">
                          Bl. {doc.folioNumber}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDateTime(doc.createdAt)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.modules.map((m) => (
                        <span
                          key={m}
                          className="rounded bg-muted px-2 py-0.5 text-xs"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                    {/* Document files with OCR status */}
                    {doc.documents && doc.documents.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {doc.documents.map((file) => (
                          <span
                            key={file.id}
                            id={`document-file-${file.id}`}
                            className={`flex items-center gap-1.5 rounded border px-2 py-1 text-xs ${
                              isFileHighlighted(file.id) ? 'border-primary bg-primary/10' : ''
                            }`}
                            title={`OCR: ${file.ocrStatus}${file.ocrText ? ' - Text erkannt' : ''}`}
                          >
                            <FileText className="h-3 w-3" />
                            <span className="max-w-32 truncate">{file.filename}</span>
                            {getOcrStatusIcon(file.ocrStatus)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {doc.documents?.[0] && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDownload(doc.documents![0].id, doc.documents![0].filename)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Grouped by Needle */}
      <div className="grid gap-6 lg:grid-cols-3">
        {[1, 2, 3].map((needle) => (
          <Card key={needle} className={`border-${needle === 1 ? 'blue' : needle === 2 ? 'green' : 'orange'}-500/30`}>
            <CardHeader className={`${getNeedleColor(needle)}/10`}>
              <CardTitle className="flex items-center gap-2 text-base">
                <span className={`h-3 w-3 rounded-full ${getNeedleColor(needle)}`} />
                {getNeedleLabel(needle)}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {!groupedByNeedle[needle]?.length ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Keine Dokumente
                </p>
              ) : (
                <div className="space-y-2">
                  {groupedByNeedle[needle].map((doc: RecordItem) => (
                    <div
                      key={doc.id}
                      className="flex items-center gap-3 rounded border p-2 text-sm hover:bg-muted/50"
                    >
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{doc.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(doc.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
